import { useEffect } from 'react';
import type { CanvasState } from './useCanvasTransform';

export function useRectDraw(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  getCanvasState: () => CanvasState,
  begin: (x: number, y: number) => void,
  update: (x: number, y: number, shiftKey?: boolean) => void,
  commit: () => void,
  // circle 전용 콜백들(Shift로 전환)
  beginCircle: (x: number, y: number) => void,
  updateCircle: (x: number, y: number, shiftKey?: boolean) => void,
  commitCircle: () => void,
  cancelTemp: () => void,
  renderNow?: () => void,
  getActiveTool?: () => 'select' | 'rectangle' | 'circle' | 'text' | 'artboard',
  shouldBlockDraw?: () => boolean,
  isDrawingRef?: React.MutableRefObject<boolean>
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let isDrawing = false;
    let drawingCircle = false;

    const toWorld = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const cx = clientX - rect.left;
      const cy = clientY - rect.top;
      const { scale, offset } = getCanvasState();
      return {
        x: (cx - offset.x) / scale,
        y: (cy - offset.y) / scale
      };
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return; // 좌클릭만
      if (shouldBlockDraw && shouldBlockDraw()) return;
      // 중간버튼 패닝/Space 패닝은 별도 훅에서 처리되므로 여기서는 단순 시작
      const p = toWorld(e.clientX, e.clientY);
      const tool = getActiveTool ? getActiveTool() : (e.shiftKey ? 'circle' : 'rectangle');
      if (tool === 'select') return; // 선택 툴일 때는 드로잉 비활성
      if (tool === 'text') return; // 텍스트 툴은 별도 처리
      if (tool === 'circle') {
        drawingCircle = true;
        beginCircle(p.x, p.y);
      } else {
        drawingCircle = false;
        begin(p.x, p.y);
      }
      isDrawing = true;
      if (isDrawingRef) {
        isDrawingRef.current = true;
      }
      renderNow && renderNow();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDrawing) return;
      if (shouldBlockDraw && shouldBlockDraw()) return;
      const p = toWorld(e.clientX, e.clientY);
      // Shift 키 상태를 전달하여 정사각형/정원 그리기 지원
      const shiftKey = e.shiftKey;
      if (drawingCircle) updateCircle(p.x, p.y, shiftKey);
      else update(p.x, p.y, shiftKey);
      renderNow && renderNow();
    };

    const end = () => {
      if (!isDrawing) return;
      isDrawing = false;
      if (isDrawingRef) {
        isDrawingRef.current = false;
      }
      if (drawingCircle) commitCircle();
      else commit();
      renderNow && renderNow();
    };

    const onKeyEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelTemp();
        isDrawing = false;
        if (isDrawingRef) {
          isDrawingRef.current = false;
        }
        renderNow && renderNow();
      }
    };

    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', end);
    window.addEventListener('keydown', onKeyEsc);

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('keydown', onKeyEsc);
    };
  }, [canvasRef, getCanvasState, begin, update, commit, cancelTemp, renderNow, beginCircle, updateCircle, commitCircle, getActiveTool, shouldBlockDraw, isDrawingRef]);
}


